import { type ReactNode, type RefObject, useState } from 'react'
import type { IconType } from 'react-icons'
import { type OptionItem, OptionList } from './OptionList.tsx'
import { Popover } from './Popover.tsx'

interface FieldBase {
  id: string
  label: string
  icon: IconType
  /** Icon colour class, e.g. `text-violet-fg`. */
  iconClass: string
  /** Extra words the field search matches. */
  keywords?: string
  /** The filter is on (a check / count shows in the field list). */
  active: boolean
  /** Shown after the field name in the list when active ("2"). */
  activeMeta?: string
}

/** A field whose values are picked from a searchable list (multi-select, "is any of"). */
export interface MultiField extends FieldBase {
  kind: 'multi'
  options: OptionItem[]
  onToggle: (value: string) => void
  /** Placeholder for the value search ("Search people"). */
  placeholder?: string
}

/** An on/off filter: picking it in the field list flips it and closes the menu. */
export interface ToggleField extends FieldBase {
  kind: 'toggle'
  onToggle: () => void
}

/** A field with its own value UI (e.g. created date presets + custom range). */
export interface CustomField extends FieldBase {
  kind: 'custom'
  render: (ctx: { close: () => void; back?: () => void }) => ReactNode
}

export type FilterField = MultiField | ToggleField | CustomField

export interface AddFilterMenuProps {
  anchor: RefObject<HTMLElement | null>
  fields: FilterField[]
  /** Open straight on this field's values (editing a chip); otherwise the field list. */
  fieldId?: string
  onClose: () => void
  /** Where focus goes on close if the anchor has unmounted. */
  returnFocus?: RefObject<HTMLElement | null>
}

/**
 * "Add filter" popover: a searchable list of fields; picking one shows its values in the same
 * popover. Multi-value fields stay open so several values can be ticked; Esc, an outside click
 * or picking a single-choice value closes it.
 */
export function AddFilterMenu({
  anchor,
  fields,
  fieldId,
  onClose,
  returnFocus,
}: AddFilterMenuProps) {
  const [current, setCurrent] = useState<string | undefined>(fieldId)
  const field = fields.find((f) => f.id === current)
  const fromList = fieldId === undefined
  const back = fromList ? () => setCurrent(undefined) : undefined

  let content: ReactNode
  if (!field) {
    content = (
      <OptionList
        label="Filter fields"
        items={fields.map((f) => ({
          id: f.id,
          label: f.label,
          keywords: f.keywords,
          leading: <f.icon className={f.iconClass} />,
          meta: f.active ? (f.activeMeta ?? 'On') : undefined,
          submenu: f.kind !== 'toggle',
          selected: f.kind === 'toggle' && f.active,
        }))}
        onPick={(id) => {
          const f = fields.find((x) => x.id === id)
          if (!f) return
          if (f.kind === 'toggle') {
            f.onToggle()
            onClose()
          } else setCurrent(id)
        }}
      />
    )
  } else if (field.kind === 'multi') {
    content = (
      <OptionList
        key={field.id}
        label={field.label}
        multi
        placeholder={field.placeholder ?? `Search ${field.label.toLowerCase()}`}
        items={field.options}
        onPick={field.onToggle}
        onBack={back}
        backLabel={`${field.label}`}
        onEmptyBackspace={back}
      />
    )
  } else if (field.kind === 'custom') {
    content = (
      <div key={field.id} className="flex min-h-0 flex-col">
        {field.render({ close: onClose, back })}
      </div>
    )
  }

  return (
    <Popover
      anchor={anchor}
      onClose={onClose}
      returnFocus={returnFocus}
      label={field ? `${field.label} filter` : 'Add filter'}
      width={field ? 288 : 248}
    >
      {content}
    </Popover>
  )
}
